import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { StatusBadge } from "../StatusBadge";
import { LineageIndicator } from "./LineageIndicator";

function minutesOf(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}
function durationMinutes(start, end) {
  const s = minutesOf(start), e = minutesOf(end);
  if (s == null || e == null) return null;
  return e >= s ? e - s : e + 24 * 60 - s;
}
function formatDiff(actualStart, actualEnd, schedStart, schedEnd) {
  const actual = durationMinutes(actualStart, actualEnd);
  const scheduled = durationMinutes(schedStart, schedEnd);
  if (actual == null || scheduled == null) return "-";
  const diff = actual - scheduled;
  if (diff === 0) return "0m";
  return diff > 0 ? `+${diff}m` : `${diff}m`;
}

/** One expandable row of the Daily Attendance table - collapsed shows only the 6 summary
 * columns the spec asked for; every raw OCR/correction/exception field lives behind the
 * expand toggle so the main table never gets overwhelming. */
export const DailyRecordRow = ({ row, review, isLocked, onCorrect }) => {
  const [expanded, setExpanded] = useState(false);
  const hasCorrection = (row.correction_ids || []).length > 0;
  const correctedIn = hasCorrection ? row.manager_approved_clock_in : null;
  const correctedOut = hasCorrection ? row.manager_approved_clock_out : null;

  return (
    <>
      <tr className="border-t border-gray-100" data-testid={`day-row-${row.date}`}>
        <td className="px-3 py-2">
          <button onClick={() => setExpanded((v) => !v)} className="flex items-center gap-1 font-medium text-gray-700" data-testid={`day-row-expand-${row.date}`}>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {row.date}
          </button>
        </td>
        <td className="px-3 py-2 text-xs text-gray-500">{row.scheduled_start && row.scheduled_end ? `${row.scheduled_start}-${row.scheduled_end}` : "-"}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{row.extracted_clock_in || "?"}-{row.extracted_clock_out || "?"}</td>
        <td className="px-3 py-2 text-xs font-semibold text-gray-800">{hasCorrection ? `${correctedIn || "?"}-${correctedOut || "?"}` : "-"}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{formatDiff(row.manager_approved_clock_in || row.extracted_clock_in, row.manager_approved_clock_out || row.extracted_clock_out, row.scheduled_start, row.scheduled_end)}</td>
        <td className="px-3 py-2"><StatusBadge status={row.classification} /></td>
        <td className="px-3 py-2">
          {!isLocked && (
            <button data-testid={`correct-day-btn-${row.date}`} onClick={() => onCorrect(row)} className="px-2 py-1 rounded text-xs font-semibold border border-gray-200 hover:bg-gray-50">Correct</button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50" data-testid={`day-row-detail-${row.date}`}>
          <td colSpan={7} className="px-3 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-2">
              <div><p className="text-gray-500">Clock-in</p><p className="font-semibold text-gray-700">{row.extracted_clock_in || "-"}</p></div>
              <div><p className="text-gray-500">Clock-out</p><p className="font-semibold text-gray-700">{row.extracted_clock_out || "-"}</p></div>
              <div><p className="text-gray-500">Break (min)</p><p className="font-semibold text-gray-700">{row.manager_approved_break_minutes ?? 0}</p></div>
              <div><p className="text-gray-500">OCR confidence</p><p className="font-semibold text-gray-700">{row.extraction_confidence || "-"}</p></div>
              <div><p className="text-gray-500">Source side</p><p className="font-semibold text-gray-700">{review.pair?.blue_file_id && review.pair?.red_file_id ? "BLUE + RED" : review.blue_file ? "BLUE" : review.red_file ? "RED" : "-"}</p></div>
              <div><p className="text-gray-500">Exception</p><p className="font-semibold text-gray-700">{row.classification === "MATCH" ? "None" : row.classification}</p></div>
              <div><p className="text-gray-500">Correction reason</p><p className="font-semibold text-gray-700">{hasCorrection ? `${row.correction_ids.length} recorded` : "None"}</p></div>
              <div><p className="text-gray-500">Notes</p><p className="font-semibold text-gray-700 truncate" title={row.notes}>{row.notes || "-"}</p></div>
            </div>
            <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Lineage for this day</p>
            <LineageIndicator review={review} row={row} />
          </td>
        </tr>
      )}
    </>
  );
};
